import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Send, Lock, RefreshCw, AlertCircle } from 'lucide-react';
import { atlasApi, AtlasUser, DiscConvite } from '../shared/atlasApi';
import LinkConvite, { BadgeConvite, dataHora } from './LinkConvite';
import RelatorioIndividual from './RelatorioIndividual';
import Fit from './Fit';

interface Props {
  user: AtlasUser;
  funccodi: number;
  podeEditar: boolean;
}

/** Bloco do RH: situação da última solicitação e botão para gerar o link. */
const PainelSolicitacao: React.FC<{ user: AtlasUser; funccodi: number }> = ({ user, funccodi }) => {
  const [ultimo, setUltimo] = useState<DiscConvite | null>(null);
  const [loading, setLoading] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const r = await atlasApi.discConvites(user, { funccodi });
      setUltimo(r.itens?.[0] || null);
    } catch (e: any) {
      setErro(e?.message || 'Erro ao carregar a solicitação.');
    } finally {
      setLoading(false);
    }
  }, [user, funccodi]);

  useEffect(() => { carregar(); }, [carregar]);

  const solicitar = async () => {
    setGerando(true);
    setErro(null);
    try {
      const r = await atlasApi.discSolicitar(user, [funccodi]);
      if (r.itens[0]) setUltimo(r.itens[0]);
      else setErro(r.ignorados[0]?.motivo || 'Não foi possível gerar o link.');
    } catch (e: any) {
      setErro(e?.message || 'Falha ao gerar o link.');
    } finally {
      setGerando(false);
    }
  };

  if (loading) return null;

  const pendente = ultimo?.status === 'pendente';

  return (
    <div className="bg-white rounded-2xl border border-purple-200 p-5 space-y-3 max-w-5xl">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[12rem]">
          <h3 className="font-semibold text-gray-900">Solicitação do questionário</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {!ultimo && 'Nenhum link enviado ainda. O colaborador responde pelo link, sem login, e não vê o resultado.'}
            {ultimo && `Último pedido em ${dataHora(ultimo.criado_em)}`}
            {ultimo?.status === 'respondido' && ` · respondido em ${dataHora(ultimo.respondido_em)}`}
          </p>
        </div>
        {ultimo && <BadgeConvite status={ultimo.status} />}
        {!pendente && (
          <button
            onClick={solicitar}
            disabled={gerando}
            className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium flex items-center gap-2"
          >
            {gerando ? <Loader2 className="w-4 h-4 animate-spin" />
              : ultimo ? <RefreshCw className="w-4 h-4" /> : <Send className="w-4 h-4" />}
            {ultimo ? 'Pedir de novo' : 'Solicitar DISC'}
          </button>
        )}
      </div>
      {pendente && ultimo && <LinkConvite user={user} convite={ultimo} onAtualizado={carregar} />}
      {erro && <div className="flex items-center gap-2 text-sm text-rose-700"><AlertCircle className="w-4 h-4" /> {erro}</div>}
    </div>
  );
};

/** Aba DISC da ficha: RH vê e solicita; gestor vê o dos liderados; os demais não veem. */
const AbaDiscFicha: React.FC<Props> = ({ user, funccodi, podeEditar }) => {
  const [podeVer, setPodeVer] = useState<boolean | null>(podeEditar ? true : null);

  useEffect(() => {
    if (podeEditar) { setPodeVer(true); return; }
    let cancel = false;
    atlasApi.discAcesso(user, funccodi)
      .then(r => { if (!cancel) setPodeVer(!!r.pode_ver); })
      .catch(() => { if (!cancel) setPodeVer(false); });
    return () => { cancel = true; };
  }, [user, funccodi, podeEditar]);

  if (podeVer === null) {
    return <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  if (!podeVer) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6 flex gap-4 max-w-2xl">
        <div className="w-10 h-10 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center shrink-0">
          <Lock className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">Resultado restrito</h3>
          <p className="text-sm text-gray-500 mt-1">O perfil DISC fica visível só para o RH e para a liderança do colaborador.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {podeEditar && <PainelSolicitacao user={user} funccodi={funccodi} />}
      {/* Sem onRefazer de propósito: quem abre a ficha não responde pelo outro. */}
      <RelatorioIndividual user={user} funccodi={funccodi} />
      <Fit user={user} funccodi={funccodi} />
    </div>
  );
};

export default AbaDiscFicha;
